import { Link } from "react-router-dom";
import { ArrowUpRight, Snowflake } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { useLang } from "../i18n";

const IMG = (id) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=1000&q=80`;

const RAW_IMAGES = {
  mais: "/assets/mais.jpg",
  cajou: "/assets/cajou-brut.jpg",
  cafe: IMG("photo-1675306408031-a9aad9f23308"),
  cacao: IMG("photo-1545567724-9b16e57c906d"),
  riz: IMG("photo-1586201375761-83865001e31c"),
  karite: "https://plus.unsplash.com/premium_photo-1676879544771-57b8c8c4447a?auto=format&fit=crop&w=1000&q=80",
  coque: "/assets/coque-cajou.jpg",
};

const FROZEN_IMAGES = {
  poissons: IMG("photo-1741739813128-cb658a9a0f9a"),
  viandes: IMG("photo-1607623814075-e51df1bdc82f"),
  autres: IMG("photo-1518843875459-f738682238a6"),
};

const REEFER_IMG = IMG("photo-1678182451047-196f22a4143e");

const ProductCard = ({ item, img, index, testId }) => {
  const { t } = useLang();
  return (
    <Reveal delay={(index % 3) * 0.07}>
      <Link to={`/produit/${item.key}`} data-testid={`${testId}-link`} className="block">
        <article
          data-testid={testId}
          className="group h-full border border-oris-green/12 bg-white transition-shadow duration-500 hover:shadow-[0_18px_50px_rgb(15,41,24,0.10)]"
        >
          <div className="spotlight-frame overflow-hidden">
            <img
              src={img}
              alt={item.name}
              loading="lazy"
              className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
          </div>
          <div className="p-6 lg:p-7">
            <span className="font-serif text-sm italic text-oris-gold">{String(index + 1).padStart(2, "0")}</span>
            <h3 className="mt-1 font-serif text-2xl font-medium text-oris-ink lg:text-[1.7rem]">{item.name}</h3>
            <p className="mt-3 text-sm leading-relaxed text-oris-moss">{item.desc}</p>
            <span className="mt-5 inline-flex items-center gap-2 text-[0.68rem] font-bold uppercase tracking-[0.18em] text-oris-green transition-colors duration-300 group-hover:text-oris-gold">
              {t.products.fiche}
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </div>
        </article>
      </Link>
    </Reveal>
  );
};

export const Products = () => {
  const { t } = useLang();
  return (
    <section id="produits" data-testid="products-section" className="bg-white py-24 lg:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead overline={t.products.overline} title={t.products.title} testId="products-title" />
        <Reveal>
          <p className="-mt-6 mb-16 max-w-2xl text-base text-oris-moss lg:text-lg">{t.products.intro}</p>
        </Reveal>

        <Reveal>
          <h3 data-testid="raw-products-label" className="mb-8 flex items-center gap-4 text-xs font-bold uppercase tracking-[0.26em] text-oris-green">
            <span className="gold-rule" aria-hidden="true" />
            {t.products.rawLabel}
          </h3>
        </Reveal>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {t.products.raw.map((item, i) => (
            <ProductCard key={item.key} item={item} img={RAW_IMAGES[item.key]} index={i} testId={`product-card-${item.key}`} />
          ))}

          <Reveal delay={0.14}>
            <div
              data-testid="cold-chain-banner"
              className="grain relative flex h-full min-h-[22rem] flex-col justify-between overflow-hidden bg-oris-green p-7 lg:row-span-2"
            >
              <div className="absolute inset-0">
                <img src={REEFER_IMG} alt="Conteneurs frigorifiques" loading="lazy" className="h-full w-full object-cover opacity-30" />
                <div className="absolute inset-0 bg-oris-green/70" />
              </div>
              <div className="relative">
                <Snowflake className="h-8 w-8 text-oris-gold" />
                <h3 className="mt-6 font-serif text-3xl font-medium leading-tight text-oris-cream lg:text-4xl">
                  {t.products.cold.title}
                </h3>
                <p className="mt-4 max-w-sm text-sm leading-relaxed text-oris-cream/75">{t.products.cold.text}</p>
              </div>
              <ul className="relative mt-10 space-y-3">
                {t.products.cold.points.map((p) => (
                  <li key={p} className="flex items-center gap-3 text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-oris-gold">
                    <span className="h-px w-6 bg-oris-gold" aria-hidden="true" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <Reveal>
          <h3 data-testid="frozen-products-label" className="mb-8 mt-20 flex items-center gap-4 text-xs font-bold uppercase tracking-[0.26em] text-oris-green">
            <span className="gold-rule" aria-hidden="true" />
            {t.products.frozenLabel}
          </h3>
        </Reveal>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {t.products.frozen.map((item, i) => (
            <ProductCard key={item.key} item={item} img={FROZEN_IMAGES[item.key]} index={i} testId={`product-card-${item.key}`} />
          ))}
        </div>
      </div>
    </section>
  );
};
